<template>
  <span class="my-2">
    <v-progress-circular v-if="isLoading" indeterminate color="secondary" size="15" />
    <template v-else>
      <div v-if="!isEditing" class="d-flex flex-row align-center justify-between">
        <span v-html="formField.formattedValue" class="custom-form-field-value"></span>
        <v-spacer />
        <v-btn
          v-if="isEditable && formField.field"
          title="Edit data"
          variant="plain"
          icon="mdi-pencil"
          size="small"
          class="edit-button"
          @click="isEditing = true" />
      </div>
      <template v-else-if="formField.field && isEditable">
        <v-form ref="form">
          <component
            :is="getComponent(formField.field.type)"
            v-bind="getComponentProps(formField.field.type)"
            v-model="localValue"
            :field="customField" />

          <div class="d-flex align-center gap-2 mb-6 mt-4">
            <v-btn size="small" variant="outlined" @click="handleCancel">Nevermind</v-btn>
            <v-btn
              size="small"
              class="bg-primary"
              @click="handleSave"
              :disabled="!canSave"
              title="Save Changes">
              Save
            </v-btn>
          </div>
        </v-form>
      </template>
    </template>
  </span>
</template>
<script>
import { CustomFieldType, isValidCustomField } from '@nova/core';
import { DateTime } from 'luxon';
import { ref, defineComponent, computed, watch } from 'vue';

export default defineComponent({
  name: 'CustomFormsMultipleTriggerFormField',
  props: {
    /*
     * FormField entity instace from custom forms
     * An extra property called dataId is the customformdata.id
     * Used to update the existing data, when not present,
     * just create a new instance of custom form data
     */
    formField: {
      type: Object,
      required: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    componentsMapping: {
      type: Object,
      required: false,
      default: () => ({})
    },
    timezone: {
      type: String,
      required: false
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: false
    }
  },
  emits: ['update:formField'],
  setup(props, { emit }) {
    const isLoading = ref(false);
    const isEditing = ref(false);
    const localValue = ref(null);

    // This is a mapping to the custom field components
    const customField = computed(() => {
      return {
        name: props.formField.id,
        order: props.formField.order,
        value: props.formField.value,
        required: props.formField.required,
        placeholder: props.formField.field.placeholder,
        type: props.formField.field.type,
        dropDownValues: props.formField.field.extraFields?.dropDownValues,
        minLengthOrValue: props.formField.field.extraFields?.minLengthOrValue,
        maxLengthOrValue: props.formField.field.extraFields?.maxLengthOrValue
      };
    });

    const canSave = computed(() => {
      let isFilled = true;

      const field = { ...customField.value, value: localValue.value };

      if (DateTime.isDateTime(field.value)) {
        field.value = field.value.toISO();
      }

      if (field.type !== CustomFieldType.Bool) {
        if (field.required && !field.value) {
          isFilled = false;
        }
      }
      return isFilled && isValidCustomField(field);
    });

    const getComponent = type => {
      return props.componentsMapping[type]?.component ?? 'v-text-field';
    };

    const getComponentProps = type => {
      const componentProps = props.componentsMapping[type]?.props || {};

      componentProps.testId = `custom-form-field-${props.formField.id}`;
      componentProps.timezone = props.timezone;
      componentProps.militaryTimeEnabled = props.militaryTimeEnabled;
      componentProps.required = customField.value.required ?? false;
      componentProps.placeholder = customField.value.placeholder;

      // This is meant for Luna which has a different component for the custom fields
      if (getComponent(type)?.name === 'BasicFormField') {
        componentProps.fieldName = props.formField.id;
        componentProps.hint = customField.value.description;
        componentProps.fieldProps = {
          timezone: props.timezone,
          militaryTimeEnabled: props.militaryTimeEnabled
        };

        if (customField.value.dropDownValues) {
          componentProps.options = [...customField.value.dropDownValues];
        }
      }

      return componentProps;
    };

    const handleSave = async () => {
      isLoading.value = true;
      try {
        const formData = {
          id: props.formField.dataId,
          formFieldId: props.formField.id,
          objectId: props.formField.objectId,
          triggerId: props.formField.triggerId,
          entityName: props.formField.entityName ?? 'appointment',
          warehouseId: props.formField.warehouseId,
          value: localValue.value
        };

        if (formData.id) {
          await window.axios.patch(`custom-forms/form-data/${formData.id}`, {
            value: formData.value
          });
        } else {
          await window.axios.post(`custom-forms/form-data`, formData);
        }

        isEditing.value = false;
        emit('update:formField', { ...formData, label: props.formField.label });
      } catch (error) {
        console.error('Error saving custom field:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleCancel = () => {
      isEditing.value = false;
      localValue.value = props.formField.value;
    };

    watch(
      () => props.formField.value,
      newValue => {
        localValue.value = newValue;
      },
      { immediate: true, deep: true }
    );

    return {
      isLoading,
      isEditing,
      handleCancel,
      handleSave,
      customField,
      getComponent,
      getComponentProps,
      localValue,
      canSave
    };
  }
});
</script>
<style lang="scss">
.custom-form-field {
  &:hover {
    .edit-button {
      opacity: 1;
    }
  }

  .edit-button {
    opacity: 0;
    padding: 0;
    background-color: $white;
    margin-left: 4px;
    transition: opacity 0.3s ease-in-out;
  }
}
</style>
